import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import ScrollingAnnouncementBanner from "../pages/submitannoncementBanner";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  RiArrowRightLine, RiStarFill, RiCalendarCheckLine, RiTeamLine, RiHeartLine,
  RiComputerLine, RiUserStarLine, RiGlobalLine, RiMapPinLine, RiAwardLine,
  RiRocketLine, RiMicLine, RiLightbulbLine, RiShakeHandsLine, RiBarChartBoxLine,
  RiTimeLine, RiGroupLine, RiMedalLine, RiArrowLeftSLine, RiArrowRightSLine,
  RiBellLine, RiMailLine, RiCheckLine, RiCloseLine, RiVidiconLine,
  RiMoneyDollarCircleLine, RiBarChartGroupedLine, RiStarLine, RiRefreshLine,
  RiImageLine, RiCalendarLine, RiArrowDownSLine, RiArrowUpSLine, RiEyeLine,
  RiLinkedinBoxLine, RiTwitterXLine, RiUserVoiceLine, RiPriceTagLine, RiFileTextLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';

const colors = {
  accentGoldLight: "#E8B520"
}
const T = {
  white: "#FFFFFF", offWhite: "#F4F9F8", softTeal: "#E8F5F3",
  primary: "#2A9D8F", primaryDark: "#1F7A6E", primaryLight: "#3BBCAE",
  navy: "#1A3A4A", navyDeep: "#0D2B3E", accent: "#3BBCAE",
  accentGold: "#D4A017", accentGoldDark: "#B8880F", accentGoldLight: "#E8B520",
  text: "#1C3461", textMid: "#2D4A5A", textMuted: "#6B8090",
  border: "#C8E8E4", borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)", shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
};

const whyAttend = [
  { icon: RiMicLine, title: "50+ World-Class Speakers", desc: "Learn from CEOs, founders, and global thought leaders shaping industries." },
  { icon: RiGroupLine, title: "5,000+ Leaders Networking", desc: "Build relationships with decision-makers from 30+ countries worldwide." },
  { icon: RiLightbulbLine, title: "Cutting-Edge Insights", desc: "Get ahead with actionable strategies, trends, and breakthrough ideas." },
  { icon: RiShakeHandsLine, title: "Exclusive Partnerships", desc: "Close deals, forge alliances, and open doors you never knew existed." },
  { icon: RiAwardLine, title: "Certified Excellence", desc: "Earn recognized credentials and showcase your leadership journey." },
  { icon: RiRocketLine, title: "Career Acceleration", desc: "Transform your trajectory with mentorship, visibility, and opportunity." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeScale = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (d = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.5, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

const fallbackTopics = [
  "Fundamental & Cellular Immunology", "Clinical & Translational Immunology",
  "Immunotherapy & Therapeutics", "Immunology of Infectious Diseases",
  "Vaccinology & Emerging Technologies", "Neuroimmunology",
  "Systems and Computational Immunology", "Environmental and Global Immunology",
  "Developmental & Pediatric Immunology", "Immunosenescence & Aging",
  "Advanced Molecular Immunology", "Diagnostics & Biomarker Discovery",
  "Next-Gen Immunotherapies", "Global Health & Immunity",
  "Host–Microbiome–Immune Axis", "Psychoneuroimmunology",
  "Precision and Personalized Immunology", "Immunology and Chronic Inflammation",
  "Viral Immunology", "Gene & Cell Engineering for Immune Modulation",
  "Immunoengineering & Bioinformatics", "Immunology Education",
];

const TOPICS_INITIAL_COUNT = 10;

function useCountdown(target) {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, new Date(target) - new Date());
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return time;
}

const topicColors = [
  { bg: "#EFF6FF", border: "#BFDBFE", accent: "#2A9D8F", text: "#1D4ED8", num: "#DBEAFE" },
  { bg: "#F0FDF4", border: "#BBF7D0", accent: "#2A9D8F", text: "#15803D", num: "#DCFCE7" },
  { bg: "#FFF7ED", border: "#FED7AA", accent: "#D4A017", text: "#C2410C", num: "#FFEDD5" },
  { bg: "#FDF4FF", border: "#E9D5FF", accent: "#2A9D8F", text: "#7E22CE", num: "#F3E8FF" },
  { bg: "#FFFBEB", border: "#FDE68A", accent: "#D4A017", text: "#B45309", num: "#FEF3C7" },
  { bg: "#F0F9FF", border: "#BAE6FD", accent: "#2A9D8F", text: "#0369A1", num: "#E0F2FE" },
  { bg: "#FFF1F2", border: "#FECDD3", accent: "#2A9D8F", text: "#BE123C", num: "#FFE4E6" },
  { bg: "#F0FDFA", border: "#99F6E4", accent: "#2A9D8F", text: "#0F766E", num: "#CCFBF1" },
  { bg: "#FFF5F5", border: "#FEB2B2", accent: "#D4A017", text: "#9B2C2B", num: "#FED7D7" },
  { bg: "#EBF4FF", border: "#BEE3F8", accent: "#2A9D8F", text: "#2C5282", num: "#BEE3F8" },
];

function TopicsAccordion({ topics }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const displayed = showAll
    ? topics
    : topics.slice(0, TOPICS_INITIAL_COUNT);

  const hasMoreTopics = topics.length > TOPICS_INITIAL_COUNT;

  const toggle = (i) => {
    setOpenIndex(prev => prev === i ? null : i);
  };

  const titleStyle = {
    fontSize: 18,
    fontWeight: 600,
    color: T.text,
    lineHeight: 1.6,
    letterSpacing: "0.01em",
  };

  return (
    <div>
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 10
      }}>
        {displayed.map((topic, i) => {
          const isOpen = openIndex === i;
          const palette = topicColors[i % topicColors.length];

          const topicName =
            typeof topic === "string"
              ? topic
              : topic.name;

          const topicDesc =
            typeof topic === "string"
              ? ""
              : topic.description;

          const displayDesc = topicDesc
            ? topicDesc
            : `This session covers the latest advances in ${topicName}, featuring expert presentations, interactive discussions, and collaborative workshops open to all registered delegates.`;

          return (
            <div
              key={i}
              style={{
                border: `1.5px solid ${
                  isOpen ? palette.accent : palette.border
                }`,
                borderRadius: 12,
                overflow: "hidden",
                transition: "border-color 0.25s, box-shadow 0.25s",
                boxShadow: isOpen
                  ? `0 4px 20px ${palette.accent}18`
                  : "none"
              }}
            >
              <button
                type="button"
                onClick={() => toggle(i)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  background: isOpen
                    ? palette.bg
                    : T.white,
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left"
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  minWidth: 0
                }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: isOpen
                        ? palette.accent
                        : palette.num,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 800,
                      color: isOpen
                        ? "#fff"
                        : palette.accent,
                      flexShrink: 0
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>

                  <span
                    style={{
                      ...titleStyle,
                      fontWeight: isOpen ? 700 : 500,
                      color: isOpen
                        ? palette.text
                        : T.text,
                      lineHeight: 1.4,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      fontSize: 18
                    }}
                  >
                    {topicName}
                  </span>
                </div>

                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: isOpen
                      ? palette.accent
                      : palette.num,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginLeft: 12
                  }}
                >
                  <span
                    style={{
                      color: isOpen
                        ? "#fff"
                        : palette.accent,
                      fontSize: 16,
                      lineHeight: 1,
                      display: "flex"
                    }}
                  >
                    {isOpen
                      ? <RiArrowUpSLine />
                      : <RiArrowDownSLine />
                    }
                  </span>
                </div>
              </button>

              <div
                style={{
                  maxHeight: isOpen ? 200 : 0,
                  overflow: "hidden",
                  transition:
                    "max-height 0.35s cubic-bezier(0.22,1,0.36,1)"
                }}
              >
                <div
                  style={{
                    padding: "14px 20px 18px 70px",
                    background: palette.bg,
                    borderTop:
                      `1px dashed ${palette.border}`
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start"
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        minHeight: 40,
                        borderRadius: 4,
                        background: palette.accent,
                        flexShrink: 0,
                        marginTop: 2
                      }}
                    />

                    <p
                      style={{
                        fontSize: 18,
                        color: T.textMuted,
                        lineHeight: 1.75,
                        margin: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word"
                      }}
                    >
                      {displayDesc}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* VIEW ALL BUTTON */}
     {/* VIEW ALL BUTTON */}
{hasMoreTopics && (
  <div
    style={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginTop: 28,
      paddingBottom: 10
    }}
  >
    <button
      type="button"
      onClick={() => {
        setShowAll(prev => !prev);
        setOpenIndex(null);
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "12px 28px",
        minHeight: 44,
        border: "none",
        borderRadius: "999px",
        background: T.gradGold,        // solid gold gradient — always visible
        color: T.navyDeep,             // dark navy text on gold — guaranteed contrast
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1.2,
        cursor: "pointer",
        letterSpacing: "0.02em",
        boxSizing: "border-box",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        transition: "transform 0.2s, box-shadow 0.2s"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)";
      }}
    >
      {showAll ? (
        <RiArrowUpSLine size={18} color={T.navyDeep} />
      ) : (
        <RiArrowDownSLine size={18} color={T.navyDeep} />
      )}

      <span style={{ color: T.navyDeep }}>
        {showAll ? "Show fewer topics" : `View all ${topics.length} topics`}
      </span>
    </button>
  </div>
)}
    </div>
  );
}
function AboutSection({ aboutData }) {
  const [expanded, setExpanded] = useState(false);

  const fullDescription = aboutData?.mainDescription || "";
  const missionDescription = aboutData?.missionDescription || "";
  const visionDescription = aboutData?.visionDescription || "";
  const missionTitle = aboutData?.missionTitle || "Our Mission";
  const visionTitle = aboutData?.visionTitle || "Our Vision";

  const splitIntoParagraphs = (text) => {
    if (!text) return [];
    if (text.includes('\n\n')) {
      return text.split('\n\n').filter(p => p.trim());
    }
    const sentences = text.match(/[^.!?]+[.!?]+[\s]*/g) || [text];
    const paras = [];
    for (let i = 0; i < sentences.length; i += 2) {
      const para = (sentences[i] || "").trim();
      const next = sentences[i + 1] || "";
      if (para || next) {
        paras.push((para + next).trim());
      }
    }
    return paras.filter(Boolean);
  };

  let paragraphs = splitIntoParagraphs(fullDescription);

  if (paragraphs.length === 0) {
    return (
      <div style={{ color: T.textMuted, lineHeight: 1.9, fontSize: 15 }}>
        <p>No description provided.</p>
      </div>
    );
  }

  const INITIAL_PARAS = 2;
  const isLong = paragraphs.length > INITIAL_PARAS;
  const displayedParas = expanded ? paragraphs : paragraphs.slice(0, INITIAL_PARAS);

  return (
    <div style={{ color: T.textMuted, lineHeight: 1.9, fontSize: 15 }}>
      {displayedParas.map((para, i) => (
        <p key={i} style={{ marginBottom: 16, wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}>
          {para}
        </p>
      ))}
      
      {isLong && (
        <button 
          onClick={() => setExpanded(prev => !prev)} 
          style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            justifyContent: "center",
            gap: 8, 
            marginBottom: 20, 
            background: "#E8B520",
            border: "none", 
            color: "#000000",
            fontWeight: 600, 
            fontSize: 14, 
            cursor: "pointer", 
            padding: "8px 16px",
            borderRadius: 6,
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#D4A81C"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#E8B520"}
        >
          {expanded ? <RiArrowUpSLine size={18} /> : <RiArrowDownSLine size={18} />}
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      
      {(missionDescription || visionDescription) && expanded && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {missionDescription && (
            <div style={{ background: `${T.primary}08`, border: `1px solid ${T.primary}22`, borderRadius: 10, padding: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: T.primary, marginBottom: 6 }}>
                {missionTitle}
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.7, wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}>
                {missionDescription}
              </p>
            </div>
          )}
          {visionDescription && (
            <div style={{ background: `${T.primaryLight}08`, border: `1px solid ${T.primaryLight}22`, borderRadius: 10, padding: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: T.primaryLight, marginBottom: 6 }}>
                {visionTitle}
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.7, wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}>
                {visionDescription}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpeakerCard({ speaker, onClick }) {
  const getSpeakerType = () => {
    if (speaker.speakerType === "keynote" || speaker.category === "keynote" || speaker.type === "keynote") return "keynote";
    return "plenary";
  };
  const isKeynote = getSpeakerType() === "keynote";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
      onClick={() => onClick(speaker)}
      style={{ background: T.white, borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", cursor: "pointer", transition: "all 0.3s ease", border: `1px solid ${T.borderLight}` }}
      whileHover={{ y: -8, boxShadow: "0 12px 30px rgba(0,0,0,0.15)" }}
    >
      <div style={{ position: "relative", paddingTop: "100%", background: `linear-gradient(135deg, ${T.primary}20, ${T.primary}05)`, overflow: "hidden" }}>
        <img src={speaker.imageUrl} alt={speaker.name}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s ease" }}
          onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&background=2A9D8F&color=fff&size=400`; }} />
        <div style={{ position: "absolute", top: 12, right: 12, background: isKeynote ? T.accentGold : T.primary, color: T.white, padding: "4px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {isKeynote ? "Keynote" : "Plenary"}
        </div>
      </div>
      <div style={{ padding: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4, lineHeight: 1.3 }}>{speaker.name}</h3>
        <p style={{ fontSize: 13, color: isKeynote ? T.accentGold : T.primary, fontWeight: 500, marginBottom: 8 }}>{speaker.role}</p>
        <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.4 }}>{speaker.company}</p>
        {speaker.expertise && speaker.expertise.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {speaker.expertise.slice(0, 2).map((exp, idx) => (
              <span key={idx} style={{ fontSize: 10, background: isKeynote ? `${T.accentGold}15` : `${T.primary}15`, color: isKeynote ? T.accentGold : T.primary, padding: "3px 8px", borderRadius: 12, fontWeight: 500 }}>{exp}</span>
            ))}
            {speaker.expertise.length > 2 && <span style={{ fontSize: 10, background: T.offWhite, color: T.textMuted, padding: "3px 8px", borderRadius: 12 }}>+{speaker.expertise.length - 2}</span>}
          </div>
        )}
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, color: isKeynote ? T.accentGold : T.primary, fontSize: 12, fontWeight: 500 }}>
          <span>Click to view details</span><RiArrowRightLine size={12} />
        </div>
      </div>
    </motion.div>
  );
}

const SpeakerDetailModal = ({ speaker, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  if (!speaker) return null;
  const isKeynote = (speaker.speakerType === "keynote" || speaker.category === "keynote" || speaker.type === "keynote");
  const typeColor = isKeynote ? T.accentGold : T.primary;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", cursor: "pointer" }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }}
        style={{ maxWidth: 900, width: "100%", maxHeight: "85vh", background: T.white, borderRadius: 24, overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", cursor: "default", position: "relative" }}
        onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: "absolute", top: 20, right: 20, width: 40, height: 40, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", color: T.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, backdropFilter: "blur(4px)" }}>
          <RiCloseLine size={20} />
        </button>
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", background: `linear-gradient(135deg, ${typeColor}15, ${typeColor}05)`, padding: 30, gap: 30, flexWrap: "wrap" }}>
            <div style={{ width: 160, height: 160, borderRadius: "50%", overflow: "hidden", background: T.white, boxShadow: "0 8px 20px rgba(0,0,0,0.15)", flexShrink: 0 }}>
              <img src={speaker.imageUrl} alt={speaker.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&background=2A9D8F&color=fff&size=200`; }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "inline-block", background: isKeynote ? T.accentGold : T.primary, color: T.white, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "uppercase", marginBottom: 12 }}>
                {isKeynote ? "🎤 Keynote Speaker" : "🎤 Plenary Speaker"}
              </div>
              <h2 style={{ fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 700, color: T.text, marginBottom: 8, fontFamily: "Playfair Display, serif" }}>{speaker.name}</h2>
              <p style={{ fontSize: 16, color: typeColor, fontWeight: 600, marginBottom: 4 }}>{speaker.role}</p>
              <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 16 }}>{speaker.company}</p>
              <div style={{ display: "flex", gap: 12 }}>
                {speaker.linkedinUrl && <a href={speaker.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: "50%", background: "#0077B5", display: "flex", alignItems: "center", justifyContent: "center", color: T.white }}><RiLinkedinBoxLine size={18} /></a>}
                {speaker.twitterUrl && <a href={speaker.twitterUrl} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: "50%", background: "#1DA1F2", display: "flex", alignItems: "center", justifyContent: "center", color: T.white }}><RiTwitterXLine size={18} /></a>}
                {speaker.website && <a href={speaker.website} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: "50%", background: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", color: T.white }}><RiGlobalLine size={18} /></a>}
              </div>
            </div>
          </div>
          <div style={{ padding: 30, overflowY: "auto", maxHeight: "calc(85vh - 220px)" }}>
            {speaker.bio && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><RiUserVoiceLine size={20} color={typeColor} /> Biography</h3>
                <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.6 }}>{speaker.bio}</p>
              </div>
            )}
            {speaker.expertise && speaker.expertise.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><RiPriceTagLine size={20} color={typeColor} /> Areas of Expertise</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                  {speaker.expertise.map((exp, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.offWhite, borderRadius: 8, fontSize: 13, color: T.textMid }}>
                      <span style={{ color: typeColor, fontSize: 14 }}>✓</span>{exp}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {speaker.achievements && speaker.achievements.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><RiAwardLine size={20} color={typeColor} /> Key Achievements</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {speaker.achievements.map((achievement, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ color: T.accentGold }}>🏆</span><span style={{ fontSize: 13, color: T.textMid }}>{achievement}</span></div>
                  ))}
                </div>
              </div>
            )}
            {speaker.publications && speaker.publications.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><RiFileTextLine size={20} color={typeColor} /> Key Publications</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {speaker.publications.slice(0, 5).map((pub, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ color: T.primary }}>📄</span><span style={{ fontSize: 13, color: T.textMid }}>{pub}</span></div>
                  ))}
                  {speaker.publications.length > 5 && <div style={{ fontSize: 12, color: T.textMuted, paddingLeft: 26 }}>+{speaker.publications.length - 5} more publications</div>}
                </div>
              </div>
            )}
            {speaker.email && (
              <div style={{ padding: 16, background: `${typeColor}08`, borderRadius: 12, marginTop: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <RiMailLine size={18} color={typeColor} />
                  <span style={{ fontSize: 13, color: T.textMid }}>Email: <a href={`mailto:${speaker.email}`} style={{ color: typeColor, textDecoration: "none" }}>{speaker.email}</a></span>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const values = [
  { icon: RiLightbulbLine, title: "Academic Excellence", description: "Curating cutting-edge research and fostering scholarly dialogue that pushes boundaries." },
  { icon: RiComputerLine, title: "Tech Innovation", description: "Virtual attendance, interactive sessions, and seamless digital networking opportunities." },
  { icon: RiTeamLine, title: "Cross-Disciplinary", description: "Bringing together diverse fields to create a rich tapestry of ideas and collaboration." },
  { icon: RiHeartLine, title: "Passionate Service", description: "Meticulous planning so you focus on content, connections, and meaningful exchange." },
];

function SectionHeader({ eyebrow, title, description, light = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <div ref={ref} style={{ textAlign: "center", marginBottom: 64 }}>
      <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeScale} custom={0} className="pill"
        style={{ marginBottom: 18, display: "inline-flex", background: light ? "rgba(255,255,255,0.12)" : T.softTeal, color: light ? "#FFFFFF" : T.primary, borderColor: light ? "rgba(255,255,255,0.2)" : T.border }}>
        {eyebrow}
      </motion.div>
      <motion.h2 initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={0.08}
        style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(32px, 4vw, 50px)", fontWeight: 700, color: light ? "#FFFFFF" : T.text, lineHeight: 1.18, letterSpacing: "-0.02em", marginBottom: 14 }}>
        {title}
      </motion.h2>
      <motion.div initial={{ scaleX: 0 }} animate={inView ? { scaleX: 1 } : {}} transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: 56, height: 3, background: light ? T.accentGold : T.gradPrimary, borderRadius: 2, margin: "0 auto 18px", transformOrigin: "left" }} />
      {description && (
        <motion.p initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} custom={0.18}
          style={{ color: light ? "rgba(255,255,255,0.72)" : T.textMuted, fontSize: 17, maxWidth: 580, margin: "0 auto", lineHeight: 1.75, fontWeight: 400 }}>
          {description}
        </motion.p>
      )}
    </div>
  );
}

function ValueCard({ value, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const IconComponent = value.icon;
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ background: T.white, borderRadius: 16, border: `1.5px solid ${T.border}`, boxShadow: T.shadow, padding: "36px 28px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradPrimary }} />
      <div style={{ width: 68, height: 68, borderRadius: 16, background: T.softTeal, border: `1.5px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px" }}>
        <IconComponent size={30} color={T.primary} />
      </div>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 21, fontWeight: 700, color: T.text, marginBottom: 12, letterSpacing: "-0.01em" }}>{value.title}</h3>
      <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.7 }}>{value.description}</p>
    </motion.div>
  );
}

function PricingCard({ plan, index, color, eventId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  const isFeatured = plan.isFeatured || plan.featured || false;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeScale}
      custom={index * 0.12}
      className="pricing-card"
      style={{
        background: isFeatured ? T.navy : "#FFFFFF",
        border: isFeatured ? "none" : `1.5px solid ${T.border}`,
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: isFeatured ? "0 24px 64px rgba(13,43,62,0.30)" : T.shadow,
        position: "relative",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-8px)";
        e.currentTarget.style.boxShadow = isFeatured
          ? "0 32px 80px rgba(13,43,62,0.40)"
          : "0 32px 80px rgba(13,43,62,0.25)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = isFeatured
          ? "0 24px 64px rgba(13,43,62,0.30)"
          : T.shadow;
      }}
    >
      {isFeatured && (
        <div
          style={{
            background: T.gradGold,
            color: T.navyDeep,
            textAlign: "center",
            padding: "8px",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          ★ MOST POPULAR
        </div>
      )}

      <div style={{ padding: "32px 30px 36px" }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: isFeatured ? "rgba(255,255,255,0.6)" : T.textMuted,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          {plan.title}
        </div>

        <div
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 54,
            fontWeight: 700,
            color: isFeatured ? "#FFFFFF" : T.primary,
            lineHeight: 1,
            marginBottom: 8,
            letterSpacing: "-0.02em",
          }}
        >
          {plan.priceDisplay || `$${plan.price}`}
        </div>

        {plan.per && (
          <div
            style={{
              fontSize: 14,
              color: isFeatured ? "rgba(255,255,255,0.5)" : T.textMuted,
              marginBottom: 8,
            }}
          >
            {plan.per}
          </div>
        )}

        {plan.description && (
          <div
            style={{
              fontSize: 14,
              color: isFeatured ? "rgba(255,255,255,0.7)" : T.textMid,
              marginBottom: 24,
              lineHeight: 1.5,
            }}
          >
            {plan.description}
          </div>
        )}

        <div
          style={{
            height: 1,
            background: isFeatured ? "rgba(255,255,255,0.1)" : T.borderLight,
            margin: "24px 0",
          }}
        />

        <div style={{ marginBottom: 32 }}>
          {plan.features &&
            plan.features.map((f, fi) => (
              <div
                key={fi}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: isFeatured
                      ? "rgba(42,157,143,0.25)"
                      : T.softTeal,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <RiCheckLine
                    size={13}
                    style={{
                      color: isFeatured ? T.primaryLight : T.primary,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 14,
                    color: isFeatured ? "rgba(255,255,255,0.8)" : T.textMid,
                    fontWeight: 500,
                  }}
                >
                  {f}
                </span>
              </div>
            ))}
        </div>

        <Link
          to={eventId ? `/events?eventId=${eventId}` : "/events"}
          state={{ selectedPlan: plan }}
          style={{
            display: "block",
            textAlign: "center",
            background: isFeatured ? T.gradGold : T.gradPrimary,
            color: isFeatured ? T.navyDeep : "#FFFFFF",
            padding: "14px",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
            textDecoration: "none",
            letterSpacing: "0.02em",
            boxShadow: isFeatured
              ? "0 8px 24px rgba(212,160,23,0.35)"
              : "0 8px 24px rgba(42,157,143,0.28)",
            transition: "opacity 0.2s, transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
            e.currentTarget.style.transform = "scale(1.02)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          Register Now
        </Link>
      </div>
    </motion.div>
  );
}

/* ─── KEY DATES — now accepts eventId ──────────────────────────── */
const KeyDatesSection = ({ eventId }) => {
  const [keyDates, setKeyDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Fetch programme data and extract key dates ──
  const fetchKeyDates = async () => {
    if (!eventId) {
      setKeyDates([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(`${API_BASE}/api/programme/event/${eventId}`);

      if (data.success && data.data) {
        let programmeData = data.data;
        if (Array.isArray(programmeData) && programmeData.length > 0) {
          programmeData = programmeData[0];
        }

        const rawDates = programmeData?.keyDates || [];

        const deadlines = rawDates
          .filter((d) => d.isEnabled && d.date)
          .map((d) => ({
            date: new Date(d.date),
            label: d.title,
            icon: "📅",
          }));

        // Sort by date (earliest first)
        deadlines.sort((a, b) => a.date - b.date);

        // Format for display
        const formatted = deadlines.map((d, idx) => ({
          date: d.date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          label: d.label,
          icon: d.icon,
          accent:
            idx === 0
              ? T.primary
              : idx === 1
              ? T.accentGold
              : T.primaryLight,
        }));

        setKeyDates(formatted);
      } else {
        setKeyDates([]);
      }
    } catch (err) {
      console.error("Error fetching programme key dates:", err);
      setError("Failed to load key dates. Please try again later.");
      setKeyDates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeyDates();
  }, [eventId]);

  // ── Render ──
  if (loading) {
    return (
      <section style={{ padding: "80px 40px", background: T.gradSoft }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              border: `3px solid ${T.border}`,
              borderTopColor: T.primary,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: T.textMuted }}>Loading key dates...</p>
        </div>
      </section>
    );
  }

  return (
    <section style={{ padding: "80px 40px", background: T.offWhite }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span className="section-tag" style={{ color: T.primary }}>
            Key Dates
          </span>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(28px,4vw,42px)",
              color: T.text,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            Mark in Your Calendar
          </h2>
        </div>

        {error && (
          <div style={{ textAlign: "center", color: "#EF4444", marginBottom: 24 }}>
            {error}
          </div>
        )}

        {keyDates.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              background: T.white,
              borderRadius: 16,
              border: `1px solid ${T.border}`,
            }}
          >
            <p style={{ color: T.textMuted, fontSize: 16 }}>
              No important dates have been set for this event yet.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 24,
            }}
          >
            {keyDates.map((d, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: "28px 24px",
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = `0 12px 32px ${d.accent}22`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: d.accent,
                    borderRadius: "16px 16px 0 0",
                  }}
                />
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: `${d.accent}15`,
                    border: `2px solid ${d.accent}30`,
                    fontSize: 24,
                    marginBottom: 16,
                  }}
                >
                  {d.icon}
                </div>
                <div style={{ color: T.text, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                  {d.date}
                </div>
                <div style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.5 }}>
                  {d.label}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: d.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: T.white,
                  }}
                >
                  {i + 1}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};


/* ─── SPEAKERS SECTION WITH PAGINATION ────────────────────────── */
function SpeakersSection({ title, speakers, type }) {
  const [showAll, setShowAll] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  
  const displayedSpeakers = showAll ? speakers : speakers.slice(0, 8);
  const hasMore = speakers.length > 8;

  if (speakers.length === 0) return null;

  const handleSpeakerClick = (speaker) => {
    setSelectedSpeaker(speaker);
  };

  const handleCloseModal = () => {
    setSelectedSpeaker(null);
  };

  return (
    <>
      <section style={{ padding: "80px 40px", background: type === "plenary" ? T.offWhite : T.gradNavy}}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="section-tag" style={{ color: T.primary }}>Our</span>
            <h2 style={{ 
              fontFamily: "Playfair Display, serif", 
              fontSize: "clamp(28px,4vw,42px)", 
              color: type === "plenary" ? T.text : T.white,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}>
              {title}
            </h2>
            <p style={{
              fontSize: 14,
              color: type === "plenary" ? T.textMuted : "rgba(255,255,255,0.7)",
              maxWidth: 600,
              margin: "12px auto 0"
            }}>
              {type === "plenary" 
                ? "Distinguished speakers sharing groundbreaking insights"
                : "Renowned keynote speakers presenting cutting-edge research"}
            </p>
          </div>
          
          {/* ✅ Added className="speakers-grid" here */}
          <div className="speakers-grid" style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(4, 1fr)", 
            gap: 28,
            marginBottom: 32
          }}>
            {displayedSpeakers.map((speaker, i) => (
              <SpeakerCard key={speaker._id || i} speaker={speaker} onClick={handleSpeakerClick} />
            ))}
          </div>
          
          {hasMore && !showAll && (
            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => setShowAll(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 32px",
                  background: "transparent",
                  border: `2px solid ${T.primary}`,
                  borderRadius: 40,
                  color: T.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.primary; e.currentTarget.style.color = T.white; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.primary; }}
              >
                View All {type === "plenary" ? "Plenary" : "Keynote"} Speakers <RiArrowRightLine size={16} />
              </button>
            </div>
          )}
          
          {showAll && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button
                onClick={() => setShowAll(false)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 24px",
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  borderRadius: 40,
                  color: T.textMuted,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.color = T.primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
              >
                <RiArrowLeftSLine size={16} /> Show Less
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedSpeaker && (
        <SpeakerDetailModal speaker={selectedSpeaker} onClose={handleCloseModal} />
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN App COMPONENT — accepts eventId prop from SummitDetails
   ════════════════════════════════════════════════════════════════ */
const App = ({ summit, eventId }) => {
  const [slides, setSlides] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plenarySpeakers, setPlenarySpeakers] = useState([]);
  const [keynoteSpeakers, setKeynoteSpeakers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [aboutData, setAboutData] = useState(null);
  const [aboutLoading, setAboutLoading] = useState(true);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [speakersLoading, setSpeakersLoading] = useState(true);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const autoplayRef = useRef(null);
  const slideInterval = 5000;
  const countdown = useCountdown("2026-06-22T09:00:00");

  const [pdfSchedule, setPdfSchedule] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubscribe = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) { alert("Please fill all fields"); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formData) });
      const data = await response.json();
      if (response.ok) { alert("✅ Subscription successful! Thank you."); setFormData({ firstName: "", lastName: "", email: "" }); }
      else alert(data.message || "Subscription failed");
    } catch { alert("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!eventId) return;
    fetchActiveSlides();
    fetchSpeakers();
    fetchTopics();
    fetchAboutData();
    fetchPricingPlans();
    fetchPdfSchedule();
  }, [eventId]);

  useEffect(() => {
    if (autoplay && slides.length > 1) {
      autoplayRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slides.length), slideInterval);
    }
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [autoplay, slides.length]);

  const fetchActiveSlides = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/summit-hero-slides/active?eventId=${eventId}`);
      if (data.success) setSlides(data.data || []);
    } catch (error) {
      console.error("Error fetching slides:", error);
      setSlides([]);
    }
  };

 const fetchAboutData = async () => {
  try {
    setAboutLoading(true);
    const { data } = await axios.get(`${API_BASE}/api/about-us/event/${eventId}`);
    if (data.success && data.data) {
      const active = Array.isArray(data.data)
        ? data.data.find(a => a.isActive) || data.data[0]
        : data.data;

      if (active) {
        const description = active.mainDescription || "";
        let paragraphs = [];
        if (description.includes('\n\n')) {
          paragraphs = description.split('\n\n').filter(p => p.trim());
        } else {
          const sentenceRegex = /[^.!?]+[.!?]+\s*/g;
          const sentences = description.match(sentenceRegex) || [description];
          paragraphs = sentences.map(s => s.trim()).filter(s => s.length > 0);
        }
        active.paragraphs = paragraphs;
        setAboutData(active);
      } else {
        setAboutData(null);
      }
    }
  } catch (error) {
    console.error("Error fetching about data:", error);
  } finally {
    setAboutLoading(false);
  }
};

  const fetchTopics = async () => {
    try {
      setTopicsLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/summit-topics/active/${eventId}`);
      
      const topics = data.summitTopics || data.topics || data.data || [];
      
      if (data.success && topics.length > 0) {
        setTopics(topics.map(t => ({ 
          name: t.name, 
          description: t.description || "" 
        })));
      } else {
        setTopics(fallbackTopics.map(name => ({ name, description: "" })));
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      setTopics(fallbackTopics.map(name => ({ name, description: "" })));
    } finally {
      setTopicsLoading(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      setPricingLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/speaker-pricing/event/${eventId}`);
      if (data.success) setPricingPlans(data.data || []);
      else setPricingPlans([]);
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      setPricingPlans([
        { title: "Oral Talk", price: 699, priceDisplay: "$699", features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea / coffee breaks", "WiFi in meeting rooms"], featured: false, color: T.primary, order: 1, isActive: true },
        { title: "Student Delegate", price: 299, priceDisplay: "$299", features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea / coffee breaks", "WiFi in meeting rooms"], featured: true, color: T.primaryLight, order: 2, isActive: true },
        { title: "Delegate / Listener", price: 799, priceDisplay: "$799", features: ["Welcome coffee", "Access to All Talks and Posters", "Conference material", "Lunch, tea / coffee breaks", "WiFi in meeting rooms"], featured: false, color: T.primary, order: 3, isActive: true },
      ]);
    } finally {
      setPricingLoading(false);
    }
  };

  const fetchSpeakers = async () => {
    try {
      setSpeakersLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/summit-speakers/event/${eventId}`);
      if (data.success) {
        const speakers = data.speakers || [];

        const plenary = speakers.filter(s => {
          const type = (s.speakerType || s.category || s.type || "").toLowerCase();
          return type === "plenary" || type.includes("plenary");
        });
        const keynote = speakers.filter(s => {
          const type = (s.speakerType || s.category || s.type || "").toLowerCase();
          return type === "keynote" || type.includes("keynote");
        });

        if (plenary.length === 0 && keynote.length === 0 && speakers.length > 0) {
          const hasKeynoteInRole = speakers.some(s => s.role?.toLowerCase().includes("keynote"));
          if (hasKeynoteInRole) {
            setPlenarySpeakers(speakers.filter(s => !s.role?.toLowerCase().includes("keynote")));
            setKeynoteSpeakers(speakers.filter(s => s.role?.toLowerCase().includes("keynote")));
          } else {
            const mid = Math.ceil(speakers.length / 2);
            setPlenarySpeakers(speakers.slice(0, mid));
            setKeynoteSpeakers(speakers.slice(mid));
          }
        } else {
          setPlenarySpeakers(plenary);
          setKeynoteSpeakers(keynote);
        }

        console.log("Plenary speakers:", plenary.length);
        console.log("Keynote speakers:", keynote.length);
      }
    } catch (error) {
      console.error("Error fetching speakers:", error);
      setPlenarySpeakers([]);
      setKeynoteSpeakers([]);
    } finally {
      setSpeakersLoading(false);
    }
  };

  const fetchPdfSchedule = async () => {
    if (!eventId) return;
    setPdfLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/pdf-schedule/event/${eventId}`);
      console.log("📄 PDF Schedule data:", data);
      if (data.success && data.data) {
        setPdfSchedule(data.data);
      } else {
        setPdfSchedule(null);
      }
    } catch (error) {
      console.error("Error fetching PDF schedule:", error);
      setPdfSchedule(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = () => {
    if (pdfSchedule?.pdfUrl) {
      window.open(pdfSchedule.pdfUrl, "_blank");
    }
  };

  const nextSlide = () => { if (slides.length > 0) { setCurrentSlide(prev => (prev + 1) % slides.length); resetAutoplay(); } };
  const prevSlide = () => { if (slides.length > 0) { setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length); resetAutoplay(); } };
  const goToSlide = (index) => { setCurrentSlide(index); resetAutoplay(); };
  const resetAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (autoplay && slides.length > 1) autoplayRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slides.length), slideInterval);
  };
  const toggleAutoplay = () => {
    setAutoplay(prev => !prev);
    if (!autoplay) resetAutoplay(); else if (autoplayRef.current) clearInterval(autoplayRef.current);
  };

  const currentSlideData = slides.length > 0 ? slides[currentSlide] : null;
  const hasSlides = slides.length > 0;
  const activePricingPlans = pricingPlans.filter(plan => plan.isActive).sort((a, b) => a.order - b.order);
  return (
    <div style={{ background: T.white, color: T.text, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,400&family=DM+Serif+Display&family=Playfair+Display:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:6px;background:${T.white}}
        ::-webkit-scrollbar-thumb{background:${T.border};borderRadius:3px}
        html{scroll-behavior:smooth}
        @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .btn-primary{background:${T.gradPrimary};color:#fff;border:none;padding:12px 28px;border-radius:8px;font-weight:600;cursor:pointer;font-size:14px;transition:all 0.2s;letter-spacing:0.5px}
        .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px ${T.primary}44}
        .btn-outline{background:transparent;color:${T.primary};border:1.5px solid ${T.primary};padding:11px 26px;border-radius:8px;font-weight:600;cursor:pointer;font-size:14px;transition:all 0.2s}
        .btn-outline:hover{background:${T.primary}22;transform:translateY(-2px)}
        .section-tag{display:inline-block;color:${T.primary};font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:3px;margin-bottom:12px}
        .pill{display:inline-flex;align-items:center;gap:6px;background:${T.softTeal};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:5px 14px;border-radius:100px;border:1px solid ${T.border}}
        .lift-card{transition:transform 0.28s ease,box-shadow 0.28s ease}
        .lift-card:hover{transform:translateY(-6px);box-shadow:0 20px 56px rgba(42,157,143,0.16)!important}
        .section-rule{width:56px;height:3px;background:${T.gradPrimary};border-radius:2px}
        .dot-bg{background-image:radial-gradient(circle,#B8DDD9 1px,transparent 1px);background-size:28px 28px}

        /* ─── RESPONSIVE OVERRIDES ─── */
        .app-container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .app-section { padding: 80px 20px; }

        @media (max-width: 1024px) {
          .app-hero { padding: 60px 30px 50px 30px !important; }
          .app-about-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .app-why-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .app-newsletter-grid { grid-template-columns: 1fr !important; gap: 32px !important; padding: 40px 30px !important; }
          .app-pricing-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .app-stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }

        @media (max-width: 768px) {
          .app-hero { padding: 40px 20px 30px 20px !important; min-height: 70vh !important; }
          .app-hero h1 { font-size: clamp(2rem, 8vw, 3rem) !important; }
          .app-hero p { font-size: 0.95rem !important; }
          .app-hero .buttons { flex-direction: column; align-items: stretch; gap: 12px; }
          .app-hero .buttons a { width: 100%; justify-content: center; }
          .app-hero .nav-arrows { display: none !important; }
          .app-hero .dots { bottom: 16px !important; gap: 8px !important; }
          .app-about-grid { gap: 32px !important; }
          .app-about-stats { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .app-about-stats .stat { padding: 16px !important; }
          .app-about-stats .stat .val { font-size: 22px !important; }
          .app-topics-section { padding: 50px 20px !important; }
          .app-topics-section h2 { font-size: 24px !important; }
          .app-topics-section .desc { font-size: 14px !important; }
          .app-values-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          .app-values-grid .value-card { padding: 20px !important; }
          .app-why-grid { gap: 24px !important; }
          .app-why-grid .image-grid { height: 300px !important; grid-template-columns: 1fr 1fr !important; }
          .app-why-grid .image-grid .main { grid-row: 1 / 3 !important; }
          .app-pricing-grid { grid-template-columns: 1fr !important; max-width: 400px; margin: 0 auto; }
          .app-newsletter-grid { padding: 30px 20px !important; }
          .app-newsletter-grid .form-row { flex-direction: column; gap: 12px; }
          .app-newsletter-grid .form-row input { width: 100% !important; }
          .app-newsletter-grid h2 { font-size: 24px !important; }
          .app-keydates-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          .app-keydates-grid .date-card { padding: 20px !important; }
          .app-keydates-grid .date-card .icon { width: 44px !important; height: 44px !important; font-size: 20px !important; }
          .app-keydates-grid .date-card .date { font-size: 16px !important; }
          .app-section { padding: 50px 20px !important; }
        }

        @media (max-width: 480px) {
          .app-hero { padding: 30px 16px 24px 16px !important; min-height: 60vh !important; }
          .app-hero h1 { font-size: clamp(1.6rem, 7vw, 2.2rem) !important; }
          .app-hero .subtitle { font-size: 0.85rem !important; }
          .app-hero .location { font-size: 14px !important; }
          .app-about-stats { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .app-about-stats .stat { padding: 12px !important; }
          .app-about-stats .stat .val { font-size: 20px !important; }
          .app-about-stats .stat .label { font-size: 11px !important; }
          .app-topics-section { padding: 40px 16px !important; }
          .app-topics-section h2 { font-size: 20px !important; }
          .app-topics-section .desc { font-size: 13px !important; }
          .app-values-grid { grid-template-columns: 1fr !important; }
          .app-values-grid .value-card { padding: 16px !important; }
          .app-values-grid .value-card .icon-wrap { width: 56px !important; height: 56px !important; }
          .app-values-grid .value-card .icon-wrap svg { font-size: 24px !important; }
          .app-values-grid .value-card h3 { font-size: 18px !important; }
          .app-why-grid .image-grid { height: 250px !important; gap: 8px !important; }
          .app-why-grid .image-grid .main { grid-row: 1 / 2 !important; }
          .app-why-grid .image-grid img { height: 120px !important; }
          .app-pricing-grid { max-width: 100%; }
          .app-pricing-grid .pricing-card { padding: 20px !important; }
          .app-pricing-grid .pricing-card .price { font-size: 40px !important; }
          .app-newsletter-grid { padding: 24px 16px !important; }
          .app-newsletter-grid .form { padding: 20px 16px !important; }
          .app-newsletter-grid .form button { width: 100%; }
          .app-keydates-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .app-keydates-grid .date-card { padding: 16px !important; }
          .app-keydates-grid .date-card .icon { width: 40px !important; height: 40px !important; font-size: 18px !important; }
          .app-keydates-grid .date-card .date { font-size: 14px !important; }
          .app-keydates-grid .date-card .label { font-size: 12px !important; }
          .app-section { padding: 40px 16px !important; }
          .app-section .section-header { margin-bottom: 32px !important; }
          .app-section .section-header h2 { font-size: 24px !important; }
          .app-section .section-header p { font-size: 14px !important; }
        }

        /* ─── SPEAKERS GRID RESPONSIVE ─── */
        .speakers-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 28px;
        }
        @media (max-width: 1100px) {
          .speakers-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 800px) {
          .speakers-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
        }
        @media (max-width: 500px) {
          /* ✅ Now 2 columns on mobile */
          .speakers-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 16px !important; }
        }
        /* (Optional) if you want 1 column for very tiny screens, add another breakpoint */

        /* ─── SPEAKER DETAIL MODAL RESPONSIVE ─── */
        .speaker-modal-content {
          max-width: 900px;
          width: 100%;
          max-height: 85vh;
          overflow: hidden;
        }
        @media (max-width: 600px) {
          .speaker-modal-content { max-height: 90vh; }
          .speaker-modal-header { flex-direction: column; align-items: center; text-align: center; padding: 20px !important; gap: 16px !important; }
          .speaker-modal-header img { width: 120px !important; height: 120px !important; }
          .speaker-modal-body { padding: 20px !important; }
          .speaker-modal-close { top: 12px !important; right: 12px !important; width: 32px !important; height: 32px !important; }
        }

        /* ─── NEWSLETTER FORM RESPONSIVE ─── */
        .newsletter-form-row {
          display: flex;
          gap: 12px;
        }
        @media (max-width: 768px) {
          .newsletter-form-row { flex-direction: column; gap: 12px; }
          .newsletter-form-row input { width: 100% !important; }
        }

        /* ─── PRICING CARD RESPONSIVE ─── */
        .pricing-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        @media (max-width: 768px) {
          .pricing-card { padding: 20px !important; }
          .pricing-card .price { font-size: 40px !important; }
        }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
    <section className="app-hero" style={{ 
  position: "relative", 
  overflow: "hidden", 
  minHeight: "88vh", 
  display: "flex", 
  alignItems: "center", 
  justifyContent: "flex-start", 
  textAlign: "left", 
  padding: "80px 40px 80px 80px" 
}}>
  {hasSlides && currentSlideData?.image && (
    <div style={{ 
      position: "absolute", 
      inset: 0, 
      backgroundImage: `url(${currentSlideData.image})`, 
      backgroundSize: "cover", 
      backgroundPosition: "center", 
      zIndex: 0 
    }} />
  )}
  
  <div style={{ 
    position: "absolute", 
    inset: 0, 
    backgroundColor: "rgba(13,43,62, 0.05)",
    pointerEvents: "none", 
    zIndex: 1 
  }} />
  
  <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
    <div style={{ position: "absolute", top: "10%", left: "15%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${T.primary}18, transparent 70%)`, filter: "blur(60px)" }} />
    <div style={{ position: "absolute", bottom: "10%", right: "10%", width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${T.accentGold}18, transparent 70%)`, filter: "blur(60px)" }} />
  </div>

  {hasSlides && slides.length > 1 && (
    <>
      <button onClick={prevSlide} className="app-hero-nav" style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", zIndex: 20, background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white", backdropFilter: "blur(4px)" }}>❮</button>
      <button onClick={nextSlide} className="app-hero-nav" style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", zIndex: 20, background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white", backdropFilter: "blur(4px)" }}>❯</button>
    </>
  )}
  {hasSlides && slides.length > 1 && (
    <div className="app-hero-dots" style={{ position: "absolute", bottom: 30, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 12, zIndex: 20 }}>
      {slides.map((_, index) => (
        <button key={index} onClick={() => goToSlide(index)} style={{ width: currentSlide === index ? 40 : 10, height: 10, borderRadius: 5, background: currentSlide === index ? T.accentGold : "rgba(255,255,255,0.5)", border: "none", cursor: "pointer", transition: "all 0.3s ease" }} />
      ))}
    </div>
  )}

  <div style={{ position: "relative", maxWidth: 860, marginRight: "auto", animation: "fadeUp 0.8s ease both", zIndex: 15, textAlign: "left", width: "100%" }}>
    {currentSlideData?.description && !currentSlideData.description.toLowerCase().startsWith("slide") && (
      <div style={{ display: "inline-block", background: `${T.accentGold}33`, border: `1px solid ${T.accentGold}88`, borderRadius: 100, padding: "10px 24px", marginBottom: 24 }}>
        <span style={{ color: T.accentGoldLight, fontSize: 14, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" }}>{currentSlideData.description}</span>
      </div>
    )}
    
    {currentSlideData?.title && !currentSlideData.title.toLowerCase().startsWith("slide") && (
      <h1 className="app-hero-title" style={{ fontFamily: currentSlideData.titleFontFamily || "Playfair Display, serif", fontSize: currentSlideData.titleFontSize || "clamp(48px, 8vw, 96px)", lineHeight: 1.05, marginBottom: 24, color: currentSlideData.titleColor || "#fff", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
        {currentSlideData.title}
      </h1>
    )}
    
    {currentSlideData?.subtitle && !currentSlideData.subtitle.toLowerCase().startsWith("slide") && (
      <p className="app-hero-subtitle" style={{ fontSize: currentSlideData.subtitleFontSize || "clamp(16px, 2.5vw, 22px)", color: currentSlideData.subtitleColor || "rgba(255,255,255,0.9)", marginBottom: 12 }}>
        {currentSlideData.subtitle}
      </p>
    )}
    
    {(currentSlideData?.date || currentSlideData?.location) && (
      <p className="app-hero-location" style={{ fontSize: 16, color: T.accentGold, fontWeight: 600, marginBottom: 40, letterSpacing: 1 }}>
        📍 {currentSlideData?.date}{currentSlideData?.date && currentSlideData?.location && " · "}{currentSlideData?.location}
      </p>
    )}
    {(currentSlideData?.buttonText || currentSlideData?.secondaryButtonText) && (
      <div className="app-hero-buttons" style={{ display: "flex", gap: 16, justifyContent: "flex-start", flexWrap: "wrap" }}>
        {currentSlideData?.buttonText && (
          <a href={currentSlideData?.buttonLink || "/register"} className="btn-primary" style={{ background: currentSlideData.buttonPrimaryBg || T.gradGold, color: currentSlideData.buttonPrimaryColor || T.navyDeep, padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 600, display: "inline-block" }}>
            {currentSlideData.buttonText}
          </a>
        )}
        {currentSlideData?.secondaryButtonText && (
          <a href={currentSlideData?.secondaryButtonLink || "/submit-abstract"} className="btn-outline" style={{ background: currentSlideData.buttonSecondaryBg || "transparent", color: currentSlideData.buttonSecondaryColor || T.white, padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 600, border: `1px solid ${currentSlideData.buttonSecondaryColor || T.white}30`, display: "inline-block" }}>
            {currentSlideData.secondaryButtonText}
          </a>
        )}
      </div>
    )}
  </div>
</section>

      <ScrollingAnnouncementBanner eventId={eventId} />

      {/* ── ABOUT ───────────────────────────────────────────────────── */}
     <section className="app-section" style={{ padding: "80px 20px", maxWidth: 1200, margin: "0 auto", background: T.white }}>
  <div className="app-about-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "start" }}>
    <div style={{ animation: "slideIn 0.6s ease both" }}>
      <span className="section-tag" style={{ color: T.primary }}>About the Conference</span>
      <h2 style={{ 
        fontFamily: "Playfair Display, serif", 
        fontSize: "clamp(28px,4vw,42px)", 
        marginBottom: 20, 
        lineHeight: 1.2, 
        color: T.text,
        fontWeight: 700,
        letterSpacing: "-0.02em"
      }}>
        {aboutData?.mainTitle ? (
          aboutData.mainTitle
        ) : (
          <>Join us at <span style={{ color: T.primary }}>meetings21</span> 2026</>
        )}
      </h2>
      <AboutSection aboutData={aboutData} />
    </div>
    
    <div className="app-about-stats" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {[
        { icon: "🎤", val: "5+", label: "Plenary Presentations" },
        { icon: "🗝️", val: "10+", label: "Keynote Presentations" },
        { icon: "📊", val: "150+", label: "Technical Presentations" },
        { icon: "📋", val: "50+", label: "Poster Presentations" },
        { icon: "🏆", val: "", label: "Best Presentation Awards" },
        { icon: "🔬", val: "", label: "Live Workshops" },
      ].map((s, i) => (
        <div 
          className="stat"
          key={i} 
          style={{ 
            background: T.offWhite, 
            border: `1px solid ${T.border}`, 
            borderRadius: 12, 
            padding: "20px", 
            textAlign: "center", 
            transition: "all 0.2s" 
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
          {s.val && <div className="val" style={{ color: T.primary, fontWeight: 800, fontSize: 28, lineHeight: 1 }}>{s.val}</div>}
          <div className="label" style={{ color: T.textMuted, fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  </div>
</section>
  <SpeakersSection title="Plenary Speakers" speakers={plenarySpeakers} type="plenary" />

      {/* ── KEYNOTE SPEAKERS ───────── */}
      <SpeakersSection title="Keynote Speakers" speakers={keynoteSpeakers} type="keynote" />
      
      {/* ── TOPICS ───────────────────────────────────────────────────── */}
      <section className="app-topics-section" style={{ padding: "80px 20px", background: T.primary}}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="section-tag" style={{ color: T.white, fontSize: "clamp(16px, 2vw, 20px)", fontWeight: 600 }}>TOPICS</span>
            <h2 style={{ 
              fontFamily: "Playfair Display, serif", 
              fontSize: "clamp(24px, 3.5vw, 36px)", 
              color: T.white, 
              marginTop: 8, 
              marginBottom: 16,
              fontWeight: 700,
              letterSpacing: "-0.02em"
            }}>
              Topics Include But Are Not Limited To
            </h2>
            <p className="desc" style={{ fontSize: 18, fontWeight: 700, color: T.white, maxWidth: 700, margin: "0 auto", lineHeight: 1.5 }}>
              Explore cutting-edge research, emerging trends, and interdisciplinary discussions across the following key areas.
            </p>
          </div>
          {topicsLoading ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
              <p style={{ color: T.textMuted, fontSize: 18, fontWeight: 700 }}>Loading topics...</p>
            </div>
          ) : (
            <TopicsAccordion topics={topics} />
          )}
        </div>
      </section>

      {/* ── KEY DATES — pass eventId ──────────────────────────────────── */}
      <KeyDatesSection eventId={eventId} />

      {/* ── CORE VALUES ──────────────────────────────────────────────── */}
      <section className="app-section" style={{ padding: "96px 20px", background: T.primary }}>
        <div className="section-header" style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <SectionHeader eyebrow="Our Philosophy" title="Core Values That Drive Us" description="Passion for academic excellence drives everything we do." light={true} />
        </div>
        <div className="app-values-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 }}>
          {values.map((value, index) => <ValueCard key={index} value={value} index={index} />)}
        </div>
      </section>

      {/* ── WHY ATTEND ───────────────────────────────────────────────── */}
      <section className="app-section" style={{ padding: "96px 20px", background: T.offWhite }} className="dot-bg">
        <div className="app-why-grid" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }}>
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="image-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 12, height: 450 }}>
            <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=500&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, gridRow: "1 / 3", boxShadow: T.shadow }} />
            <img src="https://images.unsplash.com/photo-1559223607-a43c990c692c?w=400&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, boxShadow: T.shadow }} />
            <img src="https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=400&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 14, boxShadow: T.shadow }} />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <span className="pill" style={{ marginBottom: 20, display: "inline-flex" }}>Why Attend?</span>
            <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 16, letterSpacing: "-0.02em" }}>Why You Should Join Our Event?</h2>
            <div className="section-rule" style={{ marginBottom: 24 }} />
            <p style={{ color: T.textMuted, fontSize: 15, lineHeight: 1.8, marginBottom: 32 }}>Our summits deliver unparalleled knowledge, world-class networking, and career-defining opportunities — all in one extraordinary venue.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {whyAttend.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="lift-card"
                  style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "16px 18px", background: T.white, borderRadius: 12, border: `1.5px solid ${T.border}`, boxShadow: "0 2px 10px rgba(42,157,143,0.05)", cursor: "pointer" }}>
                  <div style={{ width: 44, height: 44, background: T.softTeal, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1.5px solid ${T.border}` }}>
                    <item.icon size={20} style={{ color: T.primary }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4, fontFamily: "Playfair Display, serif" }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────── */}
      <section className="app-section" style={{ padding: "80px 20px", background: T.primary }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span style={{ 
              display: "inline-block", 
              padding: "4px 16px", 
              borderRadius: 20, 
              background: "rgba(255,255,255,0.15)", 
              color: T.white, 
              fontSize: 12, 
              fontWeight: 600, 
              letterSpacing: "0.08em", 
              textTransform: "uppercase", 
              marginBottom: 12 
            }}>
              Registrations
            </span>
            <h2 style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: "clamp(28px,4vw,42px)", 
              color: T.white, 
              marginBottom: 12,
              fontWeight: 700,
              letterSpacing: "-0.02em"
            }}>
              Explore Pricing Policies
            </h2>
            <p style={{ 
              color: "rgba(255,255,255,0.8)", 
              maxWidth: 500, 
              margin: "0 auto", 
              fontSize: 16 
            }}>
              Choose the perfect plan that fits your needs
            </p>
          </div>
          
          {pricingLoading ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                border: `3px solid rgba(255,255,255,0.2)`, 
                borderTopColor: T.white, 
                borderRadius: "50%", 
                animation: "spin 1s linear infinite", 
                margin: "0 auto 16px" 
              }} />
              <p style={{ color: "rgba(255,255,255,0.7)" }}>Loading pricing plans...</p>
            </div>
          ) : activePricingPlans.length > 0 ? (
            <div className="app-pricing-grid" style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
              gap: 24 
            }}>
              {activePricingPlans.map((plan, i) => (
                <PricingCard 
                  key={plan._id || i} 
                  plan={plan} 
                  index={i}
                  color={plan.color}
                  eventId={eventId}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <p style={{ color: "rgba(255,255,255,0.7)" }}>No pricing plans available at the moment.</p>
            </div>
          )}
        </div>
        
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </section>

      {/* ── NEWSLETTER ───────────────────────────────────────────────── */}
      <section className="app-section" style={{ padding: "0 20px 96px", background: T.white, marginTop: 120 }}>
        <div className="app-newsletter-grid" style={{ maxWidth: 1100, margin: "0 auto", background: T.gradNavy, borderRadius: 24, padding: "72px 40px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center", position: "relative", overflow: "hidden", boxShadow: T.shadowLg }}>
          <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(42,157,143,0.08)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -60, left: 200, width: 200, height: 200, borderRadius: "50%", background: "rgba(212,160,23,0.06)", pointerEvents: "none" }} />
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(42,157,143,0.15)", border: "1px solid rgba(42,157,143,0.25)", borderRadius: 100, padding: "5px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.primaryLight, textTransform: "uppercase", marginBottom: 20 }}>Newsletter Sign-Up</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 700, color: T.white, marginBottom: 16, lineHeight: 1.22, letterSpacing: "-0.02em" }}>Subscribe to Get Latest News</h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.75, marginBottom: 12 }}>Stay ahead with speaker announcements, agenda updates, early-bird offers, and summit highlights delivered to your inbox.</p>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>* We never share your personal info with anyone.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="form" style={{ background: "rgba(255,255,255,0.07)", border: "1.5px solid rgba(42,157,143,0.18)", borderRadius: 16, padding: "36px 32px", backdropFilter: "blur(12px)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="newsletter-form-row" style={{ display: "flex", gap: 12 }}>
                  <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} style={{ background: "rgba(255,255,255,0.95)", flex: 1, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 14 }} />
                  <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} style={{ background: "rgba(255,255,255,0.95)", flex: 1, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 14 }} />
                </div>
                <input type="email" name="email" placeholder="Enter your email address" value={formData.email} onChange={handleChange} style={{ background: "rgba(255,255,255,0.95)", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 14 }} />
                <button onClick={handleSubscribe} disabled={loading} style={{ background: T.gradGold, color: T.navyDeep, padding: "14px 24px", borderRadius: 10, fontSize: 15, fontWeight: 700, border: "none", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: loading ? 0.7 : 1, transition: "opacity 0.2s, transform 0.2s", boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}
                  onMouseEnter={e => !loading && (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <RiMailLine size={18} />
                  {loading ? "Subscribing…" : "Subscribe Now"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { App };